import { useCallback, useEffect, useState } from 'react'
import { Confirm, Report } from 'notiflix'
import api from '../../../helpers/apiRequester'
import { Banner, Button, Card, EmptyState, Field, NativeSelect, Spinner, StatusPill } from '../../../ui'
import { CaseDrawer } from './CaseDrawer'
import { CaseSummaryCard, invalidateCaseBundle } from './CaseSummaryCard'
import { ClaimSearch } from './ClaimSearch'
import { ExtractedDataCard, TechnicalDetails } from './DetailSections'
import { LetterLinkCard, invalidateLetters } from './LetterLinkCard'
import { SentEmailModal } from './SentEmailModal'
import { Candidate, LetterTypePill, LinkBadge, ReplyDetail, TriageState, formatDateTime, humanise } from './shared'
import { describeUnlinkedReason } from '../commsShared/linkStates'
import { splitOnQuotes } from '../commsShared/deepDive'
import { MailboxRef, mailboxShort } from '../commsShared/mailboxes'
import styles from './styles.module.scss'

/** What the pane needs to know about a category to explain it. */
export interface CategoryMeta {
    key: string
    label: string
    description?: string | null
    nextActionLabel?: string | null
}

export const TRIAGE_STATE_OPTIONS: { value: TriageState; label: string }[] = [
    { value: 'pending', label: 'To do' },
    { value: 'in_progress', label: 'In progress' },
    { value: 'snoozed', label: 'Snoozed' },
    { value: 'resolved', label: 'Done' },
    { value: 'archived', label: 'Archived' },
]

export const CLASSIFICATION_SOURCES = [
    { value: 'llm', label: 'Categorised automatically' },
    { value: 'rules', label: 'Categorised by a rule' },
    { value: 'human', label: 'Categorised by a person' },
    { value: 'legacy', label: 'Imported from the old system' },
]

export const FALLBACK_CLASSIFICATIONS = ['acknowledged', 'info_requested', 'holding_response', 'settlement_offer', 'rejected', 'disputed', 'claim_not_found', 'wrong_recipient', 'auto_reply', 'noise', 'unrelated', 'other']

interface Props {
    replyId: string | null
    /** Category keys offered in the "Change category" select. */
    classifications?: string[]
    /** Labels / meanings / next steps per category key. */
    categoryMeta?: Map<string, CategoryMeta>
    mailboxes?: MailboxRef[]
    /** Called after any action that changed the email (status, category, link…). */
    onChanged?: (reply: ReplyDetail) => void
    /** "Show every email on this claim" from the case card / drawer. */
    onShowAllEmails?: (claimRef: string) => void
    /** Open another email (from the case drawer timeline). */
    onSelectReply?: (replyId: string) => void
    emptyTitle?: string
    emptyDescription?: string
    /** Verbatim quotes to mark in the plain-text body (deep dive evidence). */
    highlights?: string[]
}

/**
 * Everything about one received email: what it says, the claim it belongs to
 * (or how to link it), the details read from it, the body, and the technical
 * trail. Used by the mailbox workspace (from a conversation trail) and the
 * legacy Inbox page.
 */
export const ReplyDetailPane = ({ replyId, classifications = FALLBACK_CLASSIFICATIONS, categoryMeta, mailboxes, onChanged, onShowAllEmails, onSelectReply, emptyTitle = 'Select an email', emptyDescription = 'Pick an email to see what it says, the client and claim it relates to, and the letter we sent.', highlights = [] }: Props) => {
    const [detail, setDetail] = useState<ReplyDetail | null>(null)
    const [loading, setLoading] = useState(false)
    const [busy, setBusy] = useState<string | null>(null)
    const [classificationDraft, setClassificationDraft] = useState('')
    const [showBodyHtml, setShowBodyHtml] = useState(false)
    const [showRaw, setShowRaw] = useState(false)
    const [caseClaimRef, setCaseClaimRef] = useState<string | null>(null)
    const [sentEmailId, setSentEmailId] = useState<string | null>(null)

    const applyDetail = useCallback(
        (reply: ReplyDetail) => {
            setDetail(reply)
            setClassificationDraft(reply.classification || '')
            onChanged?.(reply)
        },
        [onChanged]
    )

    useEffect(() => {
        if (!replyId) {
            setDetail(null)
            return
        }
        let cancelled = false
        setLoading(true)
        setDetail(null)
        api({ url: 'comms/inbox/get', data: { replyId } })
            .then((resp) => {
                if (cancelled || !resp.data?.success) return
                setDetail(resp.data.reply)
                setClassificationDraft(resp.data.reply.classification || '')
                setShowBodyHtml(false)
                setShowRaw(false)
            })
            .catch((err: any) => {
                if (!cancelled) Report.failure('Load failed', err?.response?.data?.message || 'Could not load the email.', 'Okay')
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [replyId])

    const runAction = useCallback(
        async (label: string, url: string, data: Record<string, unknown>, successText?: string) => {
            if (!detail) return
            setBusy(label)
            try {
                const resp = await api({ url, data: { replyId: detail.id, ...data } })
                if (resp.data?.success !== false && resp.data?.reply) applyDetail(resp.data.reply)
                if (resp.data?.success === false) {
                    Report.warning(label, resp.data?.outcome?.error || resp.data?.message || 'The action did not complete.', 'Okay')
                } else if (successText) {
                    Report.success(label, successText, 'Okay')
                }
                invalidateCaseBundle(detail.case?.claimRef || detail.send?.claimRef)
                invalidateLetters(detail.case?.claimRef || detail.send?.claimRef)
                return resp.data
            } catch (err: any) {
                Report.failure(`${label} failed`, err?.response?.data?.message || 'Please try again.', 'Okay')
            } finally {
                setBusy(null)
            }
        },
        [detail, applyDetail]
    )

    const handleDownloadRaw = async () => {
        if (!detail) return
        setBusy('raw')
        try {
            const resp = await api({ url: 'comms/inbox/raw', data: { replyId: detail.id } })
            if (resp.data?.success && resp.data.url && !window.open(resp.data.url, '_blank')) {
                Report.info('Pop-up blocked', 'Allow pop-ups for this site to download the raw .eml.', 'Okay')
            }
        } catch (err: any) {
            Report.failure('Download failed', err?.response?.data?.message || 'Could not build the raw download link.', 'Okay')
        } finally {
            setBusy(null)
        }
    }

    const handleConfirmCandidate = (claimRef: string) =>
        Confirm.show('Link to claim', `Link this email to ${claimRef}? The other suggestions will be rejected.`, 'Link', 'Cancel', () =>
            runAction('Link', 'comms/inbox/candidate-decide', { claimRef, decision: 'confirm' }, `Linked to ${claimRef}.`)
        )

    const handleUnlink = () =>
        Confirm.show('Wrong claim', 'Remove the link between this email and its client and claim? The details read from the email are kept, and any letter link goes with it.', 'Unlink', 'Cancel', () =>
            runAction('Unlink', 'comms/inbox/unlink', {}, 'Unlinked.')
        )

    const handleLinkClaim = (claimRef: string) => runAction('Link', 'comms/inbox/link', { claimRef }, `Linked to ${claimRef}.`)

    const handleChooseLetter = (sendId: string | null) =>
        runAction('Letter', 'comms/inbox/letter-link', { sendId }, sendId ? 'Letter recorded.' : 'No longer tied to a specific letter.')

    const categoryLabel = (key: string | null | undefined) => (key ? categoryMeta?.get(key)?.label ?? humanise(key) : 'Not yet categorised')
    const classificationOptions = classifications.map((c) => ({ value: c, label: categoryLabel(c) }))
    const shortMailbox = (v: string | null | undefined) => mailboxShort(v, mailboxes)
    const suggestedCandidates = detail?.candidates.filter((c) => c.state === 'suggested') ?? []
    const detailCategory = detail?.classification ? categoryMeta?.get(detail.classification) : undefined
    // Comms management: the claim comes from the case link; older rows may only carry it via the letter.
    const linkedClaimRef = detail?.case?.claimRef || detail?.claimRef || detail?.send?.claimRef || null

    if (!replyId) return <EmptyState bare title={emptyTitle} description={emptyDescription} />
    if (loading || !detail) return <Spinner block text="Loading…" />

    return (
        <div className={styles.stack}>
            <div className={styles.detailHeader}>
                <div>
                    <h2 className={styles.detailTitle}>{detail.subject || '(no subject)'}</h2>
                    <div className={styles.detailSub}>
                        <span>
                            From <strong>{detail.lenderName || detail.send?.lenderName || detail.fromEmail}</strong>
                            {detail.lenderName || detail.send?.lenderName ? <span className={styles.mutedInline}> ({detail.fromEmail})</span> : null}
                        </span>
                        <span>{formatDateTime(detail.receivedAt)}</span>
                        {detail.sourceMailbox && <span className={styles.mutedInline}>to {shortMailbox(detail.sourceMailbox)}</span>}
                        <LinkBadge state={detail.linkState} method={detail.matchMethod} confidence={detail.matchConfidence} />
                        <LetterTypePill sendKind={detail.letter?.sendKind} how={detail.letter?.how} />
                    </div>
                </div>
                <div className={styles.actionRow}>
                    <Field label="Status" inline>
                        {(id) => <NativeSelect id={id} small value={detail.triageState} disabled={busy !== null} onChange={(e) => runAction('Status', 'comms/inbox/triage', { triageState: e.target.value })} options={TRIAGE_STATE_OPTIONS} />}
                    </Field>
                </div>
            </div>

            <Card
                padding="sm"
                title="What this email says"
                actions={
                    <div className={styles.inlineControls}>
                        <NativeSelect small aria-label="Change category" placeholder="Change category…" value={classificationDraft} onChange={(e) => setClassificationDraft(e.target.value)} options={classificationOptions} />
                        <Button size="sm" disabled={busy !== null || !classificationDraft || classificationDraft === detail.classification} onClick={() => runAction('Category', 'comms/inbox/classify', { classification: classificationDraft, source: 'human' }, `Set to "${categoryLabel(classificationDraft)}".`)}>
                            Save
                        </Button>
                    </div>
                }
            >
                <div className={styles.whatRow}>
                    <StatusPill tone={detail.classification ? 'info' : 'neutral'} title={detailCategory?.description || undefined}>
                        {categoryLabel(detail.classification)}
                    </StatusPill>
                    {detail.classificationSource && <span className={styles.mutedInline}>{CLASSIFICATION_SOURCES.find((s) => s.value === detail.classificationSource)?.label.toLowerCase() ?? detail.classificationSource}</span>}
                    {detail.extractionState === 'failed' && (
                        <StatusPill tone="bad" size="sm" title={detail.extractionError || undefined}>
                            could not be read automatically
                        </StatusPill>
                    )}
                    {detail.extractionState === 'pending' && <StatusPill size="sm">not yet read</StatusPill>}
                    {detail.extractionState === 'skipped' && <StatusPill size="sm">not read (our own email)</StatusPill>}
                </div>
                {detailCategory?.description && <p className={styles.categoryMeaning}>{detailCategory.description}</p>}
                <p className={styles.summaryText}>{detail.aiSummary || detail.extraction?.summary || (detail.extractionState === 'done' ? 'No summary was produced for this email.' : 'This email has not been read yet, so there is no summary. Use the email text below.')}</p>
                {(detailCategory?.nextActionLabel || detail.extraction?.suggestedNextAction || detail.suggestedNextAction) && (
                    <div className={styles.nextStep}>
                        <strong>Suggested next step:</strong> {detailCategory?.nextActionLabel || humanise(detail.suggestedNextAction)}
                        {detail.extraction?.suggestedNextAction ? <span className={styles.mutedInline}> — {detail.extraction.suggestedNextAction}</span> : null}
                    </div>
                )}
                {detail.extraction?.proposedCategory && (
                    <Banner tone="warn" style={{ marginTop: 10 }}>
                        This email did not fit an existing category. Suggested new category: <strong>{detail.extraction.proposedCategory}</strong>
                        {detail.extraction.proposedRationale ? ` — ${detail.extraction.proposedRationale}` : ''}. It can be added from the Categories page.
                    </Banner>
                )}
            </Card>

            {linkedClaimRef ? (
                <>
                    <CaseSummaryCard claimRef={linkedClaimRef} caseId={detail.case?.id ?? detail.caseId ?? null} matchMethod={detail.matchMethod} busy={busy !== null} onOpenCase={() => setCaseClaimRef(linkedClaimRef)} onShowAllEmails={() => onShowAllEmails?.(linkedClaimRef)} onUnlink={handleUnlink} />
                    <LetterLinkCard detail={detail} busy={busy !== null} onChoose={handleChooseLetter} onOpenSend={(id) => setSentEmailId(id)} />
                </>
            ) : (
                <Card tone="warn" padding="sm" title="Not linked to a client or claim" subtitle={describeUnlinkedReason(detail.unlinkedReason)}>
                    {suggestedCandidates.length > 0 && (
                        <table className={styles.table} style={{ marginBottom: 12 }}>
                            <thead>
                                <tr>
                                    <th>Possible claim</th>
                                    <th>How sure</th>
                                    <th>Why</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {suggestedCandidates.map((c) => (
                                    <tr key={c.id}>
                                        <td>
                                            <Button size="sm" variant="link" className={styles.mono} onClick={() => setCaseClaimRef(c.claimRef)} title="Open this claim to check the client before linking">
                                                {c.claimRef}
                                            </Button>
                                        </td>
                                        <td>{c.score}%</td>
                                        <td className={styles.mutedInline}>{describeReasons(c.reasons)}</td>
                                        <td className={styles.tdActions}>
                                            <Button size="sm" variant="primary" disabled={busy !== null} onClick={() => handleConfirmCandidate(c.claimRef)}>
                                                Link
                                            </Button>
                                            <Button size="sm" variant="link" disabled={busy !== null} onClick={() => runAction('Reject', 'comms/inbox/candidate-decide', { claimRef: c.claimRef, decision: 'reject' })}>
                                                Not this
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    <ClaimSearch busy={busy !== null} onLink={(claimRef) => Confirm.show('Link to claim', `Link this email to ${claimRef}?`, 'Link', 'Cancel', () => handleLinkClaim(claimRef))} onOpenClaim={(claimRef) => setCaseClaimRef(claimRef)} />
                </Card>
            )}

            <ExtractedDataCard detail={detail} busy={busy !== null} onUseValue={(factId, valueRaw) => runAction('Detail', 'comms/inbox/fact-decide', { factId, valueRaw })} />

            <Card
                padding="sm"
                title="The email"
                actions={
                    <div className={styles.actionRow}>
                        {detail.strippedBodyText && detail.strippedBodyText !== detail.bodyText && (
                            <Button size="sm" variant="link" onClick={() => setShowRaw((p) => !p)}>
                                {showRaw ? 'Hide quoted text' : 'Show quoted text'}
                            </Button>
                        )}
                        {detail.bodyHtml && (
                            <Button size="sm" variant="link" onClick={() => setShowBodyHtml((p) => !p)}>
                                {showBodyHtml ? 'Plain text' : 'As sent (HTML)'}
                            </Button>
                        )}
                    </div>
                }
            >
                {detail.attachments.length > 0 && (
                    <div className={styles.chips} style={{ marginBottom: 8 }}>
                        <span className={styles.mutedInline}>Attachments:</span>
                        {detail.attachments.map((a) => (
                            <StatusPill key={a.id} size="sm" tone={a.rejectedReason ? 'bad' : 'neutral'} title={a.rejectedReason ? `Not read: ${a.rejectedReason}` : a.hasExtractedText ? 'Text was read from this attachment' : undefined}>
                                {a.filename}
                                {a.pageCount ? ` · ${a.pageCount}p` : ''}
                            </StatusPill>
                        ))}
                    </div>
                )}
                {showBodyHtml && detail.bodyHtml ? (
                    <iframe className={styles.bodyHtml} title="Email HTML body" sandbox="" srcDoc={detail.bodyHtml} />
                ) : (
                    <pre className={styles.bodyText}>
                        {(() => {
                            const body = (showRaw ? detail.bodyText : detail.strippedBodyText || detail.bodyText) || ''
                            if (!body) return '(no plain-text body — try "As sent (HTML)" or download the .eml under Technical details)'
                            if (!highlights.length) return body
                            return splitOnQuotes(body, highlights).map((part, i) =>
                                part.hit ? (
                                    <mark key={i} className={styles.evidenceMark} title="Quoted by the deep dive as evidence for this thread's sub-category">
                                        {part.text}
                                    </mark>
                                ) : (
                                    <span key={i}>{part.text}</span>
                                )
                            )
                        })()}
                    </pre>
                )}
            </Card>

            <TechnicalDetails detail={detail} busy={busy} onRematch={() => runAction('Rematch', 'comms/inbox/rematch', {})} onReextract={() => runAction('Extract', 'comms/inbox/extract', { force: true })} onDownloadRaw={handleDownloadRaw} />

            <CaseDrawer
                claimRef={caseClaimRef}
                currentReplyId={replyId}
                onClose={() => setCaseClaimRef(null)}
                onSelectReply={(id) => {
                    setCaseClaimRef(null)
                    onSelectReply?.(id)
                }}
                onOpenSend={(id) => setSentEmailId(id)}
                onShowAllEmails={(claimRef) => {
                    setCaseClaimRef(null)
                    onShowAllEmails?.(claimRef)
                }}
            />
            <SentEmailModal sendId={sentEmailId} onClose={() => setSentEmailId(null)} />
        </div>
    )
}

const describeReasons = (reasons: Candidate['reasons']) => {
    if (!reasons) return ''
    if (Array.isArray(reasons)) return reasons.map((r) => humanise(String(r))).join(', ')
    if (typeof reasons === 'object') {
        const matched = (reasons as Record<string, unknown>).matched
        if (Array.isArray(matched)) return matched.map((r) => humanise(String(r))).join(', ')
        return Object.entries(reasons)
            .filter(([, v]) => v)
            .map(([k, v]) => (typeof v === 'boolean' ? humanise(k) : `${humanise(k)}: ${String(v)}`))
            .join(', ')
    }
    return String(reasons)
}
